import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ShoppingCart, Download, Filter, Palette, BarChart3, FileText, FileSpreadsheet, UserCheck, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Ventes = () => {
  const { token } = useAuth();
  const [ventes, setVentes] = useState([]);
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterMois, setFilterMois] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [paiementsSummary, setPaiementsSummary] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    mois: new Date().toISOString().slice(0, 7),
    produit_id: "",
    canal: "B2C",
    type_prix: "TTC",
    quantite: 1,
    cost_override: "",
    prix_override: "",
    mode_paiement: "liquide",
    virement_beneficiaire: "",
    client_nom: "",
  });

  // Client existant : recherche
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Debounced client search
  useEffect(() => {
    if (!clientSearchQuery || clientSearchQuery.trim().length < 2) {
      setClientSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/client-search`, { headers, params: { q: clientSearchQuery.trim() } });
        setClientSearchResults(res.data || []);
        setClientSearchOpen(true);
      } catch {
        setClientSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [clientSearchQuery]);

  const selectExistingClient = (c) => {
    setFormData((prev) => ({ ...prev, client_nom: c.nom || "" }));
    setClientSearchOpen(false);
    setClientSearchQuery(c.nom || "");
    toast.success(`Client « ${c.nom} » lié (${c.nombre_commandes || 0} commande${(c.nombre_commandes || 0) > 1 ? "s" : ""})`);
  };

  const fetchVentes = async () => {
    try {
      const url = filterMois ? `${API}/ventes?mois=${filterMois}` : `${API}/ventes`;
      const response = await axios.get(url, { headers });
      setVentes(response.data);
      // Récap paiements
      const psUrl = filterMois ? `${API}/paiements/summary?mois=${filterMois}` : `${API}/paiements/summary`;
      const ps = await axios.get(psUrl, { headers });
      setPaiementsSummary(ps.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des ventes");
    } finally {
      setLoading(false);
    }
  };

  const fetchProduits = async () => {
    try {
      const response = await axios.get(`${API}/produits`, { headers });
      setProduits(response.data);
    } catch (error) {
      console.error("Error fetching products");
    }
  };

  const selectedProduit = produits.find(p => p.id === formData.produit_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.produit_id) {
      toast.error("Veuillez sélectionner un produit");
      return;
    }

    const produit = produits.find(p => p.id === formData.produit_id);
    if (!produit) {
      toast.error("Produit non trouvé");
      return;
    }

    try {
      const payload = {
        mois: formData.mois,
        modele: produit.modele,
        couleur: produit.couleur,
        canal: formData.canal,
        type_prix: formData.type_prix,
        quantite: parseInt(formData.quantite),
        cost_override: formData.cost_override ? parseFloat(formData.cost_override) : produit.cost_unit,
        prix_override: formData.prix_override ? parseFloat(formData.prix_override) : 
          (formData.canal === "B2C" ? produit.prix_b2c_ttc : produit.prix_b2b_ht),
        mode_paiement: formData.mode_paiement,
        virement_beneficiaire: formData.mode_paiement === "virement" ? formData.virement_beneficiaire : "",
        client_nom: formData.client_nom || clientSearchQuery.trim(),
      };
      
      await axios.post(`${API}/ventes`, payload, { headers });
      toast.success("Vente enregistrée !");
      setDialogOpen(false);
      setFormData({
        mois: new Date().toISOString().slice(0, 7),
        produit_id: "",
        canal: "B2C",
        type_prix: "TTC",
        quantite: 1,
        cost_override: "",
        prix_override: "",
        mode_paiement: "liquide",
        virement_beneficiaire: "",
        client_nom: "",
      });
      setClientSearchQuery("");
      setClientSearchResults([]);
      fetchVentes();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette vente ?")) return;
    
    try {
      await axios.delete(`${API}/ventes/${id}`, { headers });
      toast.success("Vente supprimée");
      fetchVentes();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const endpoint = format === 'pdf' ? `${API}/export/ventes/pdf` : `${API}/export/ventes`;
      const response = await axios.get(endpoint, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ventes_topcocoon.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} téléchargé !`);
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  useEffect(() => {
    fetchVentes();
    fetchProduits();
  }, [filterMois]);

  // Edit paiement dialog
  const [editPaiementOpen, setEditPaiementOpen] = useState(false);
  const [editPaiementTarget, setEditPaiementTarget] = useState(null);
  const openEditPaiement = (vente) => {
    setEditPaiementTarget({
      id: vente.id,
      mode_paiement: vente.mode_paiement || "liquide",
      virement_beneficiaire: vente.virement_beneficiaire || "",
    });
    setEditPaiementOpen(true);
  };
  const submitEditPaiement = async () => {
    if (!editPaiementTarget) return;
    try {
      await axios.put(`${API}/ventes/${editPaiementTarget.id}/paiement`, {
        mode_paiement: editPaiementTarget.mode_paiement,
        virement_beneficiaire: editPaiementTarget.mode_paiement === "virement" ? editPaiementTarget.virement_beneficiaire : "",
      }, { headers });
      toast.success("Paiement modifié");
      setEditPaiementOpen(false);
      fetchVentes();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  // Group products by model for easier selection
  const productsByModel = produits.reduce((acc, p) => {
    if (!acc[p.modele]) acc[p.modele] = [];
    acc[p.modele].push(p);
    return acc;
  }, {});

  // Client-side filter by client name
  const ventesFiltered = filterClient
    ? ventes.filter(v => (v.client_nom || "").toLowerCase().includes(filterClient.toLowerCase()))
    : ventes;

  // Calculate totals
  const totals = ventes.reduce((acc, v) => ({
    quantite: acc.quantite + v.quantite,
    ca_ht: acc.ca_ht + v.ca_ht,
    ca_ttc: acc.ca_ttc + v.ca_ttc,
    marge_ht: acc.marge_ht + v.marge_ht
  }), { quantite: 0, ca_ht: 0, ca_ttc: 0, marge_ht: 0 });

  // Calculate stats by model
  const statsByModel = ventes.reduce((acc, v) => {
    if (!acc[v.modele]) {
      acc[v.modele] = { quantite: 0, ca_ht: 0, marge_ht: 0 };
    }
    acc[v.modele].quantite += v.quantite;
    acc[v.modele].ca_ht += v.ca_ht;
    acc[v.modele].marge_ht += v.marge_ht;
    return acc;
  }, {});

  // Calculate stats by color
  const statsByColor = ventes.reduce((acc, v) => {
    const key = `${v.modele}|${v.couleur}`;
    if (!acc[key]) {
      acc[key] = { modele: v.modele, couleur: v.couleur, quantite: 0, ca_ht: 0, marge_ht: 0 };
    }
    acc[key].quantite += v.quantite;
    acc[key].ca_ht += v.ca_ht;
    acc[key].marge_ht += v.marge_ht;
    return acc;
  }, {});

  const colorStats = Object.values(statsByColor).sort((a, b) => b.quantite - a.quantite);

  // Résumés par modèle (dynamiques — plus de modèles codés en dur)
  const modelStats = Object.entries(statsByModel)
    .map(([modele, s]) => ({ modele, ...s }))
    .sort((a, b) => b.quantite - a.quantite);
  const cardThemes = [
    { card: "bg-indigo-50 border-indigo-200", label: "text-indigo-600", value: "text-indigo-700", head: "bg-indigo-50 rounded-t-lg", title: "text-indigo-800" },
    { card: "bg-purple-50 border-purple-200", label: "text-purple-600", value: "text-purple-700", head: "bg-purple-50 rounded-t-lg", title: "text-purple-800" },
    { card: "bg-emerald-50 border-emerald-200", label: "text-emerald-600", value: "text-emerald-700", head: "bg-emerald-50 rounded-t-lg", title: "text-emerald-800" },
    { card: "bg-amber-50 border-amber-200", label: "text-amber-600", value: "text-amber-700", head: "bg-amber-50 rounded-t-lg", title: "text-amber-800" },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ventes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Ventes</h1>
          <p className="text-slate-500 mt-1">Gestion des ventes B2C et B2B</p>
        </div>
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Exporter en CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Exporter en PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-vente-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle vente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="vente-dialog">
              <DialogHeader>
                <DialogTitle>Enregistrer une vente</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Client existant (recommande) */}
                <div className="relative bg-indigo-50/60 border border-indigo-200 rounded-lg p-3 space-y-2" data-testid="client-search-block-ventes">
                  <Label className="flex items-center gap-2 text-indigo-700 font-semibold text-sm">
                    <UserCheck className="w-4 h-4" />
                    Client (optionnel)
                  </Label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={clientSearchQuery}
                      onChange={(e) => {
                        setClientSearchQuery(e.target.value);
                        setFormData((p) => ({ ...p, client_nom: e.target.value }));
                      }}
                      onFocus={() => clientSearchResults.length > 0 && setClientSearchOpen(true)}
                      placeholder="Rechercher un client existant ou saisir un nom…"
                      className="pl-9 bg-white"
                      data-testid="client-search-input-ventes"
                    />
                    {clientSearchOpen && clientSearchResults.length > 0 && (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="client-search-results-ventes">
                        {clientSearchResults.map((c) => (
                          <button
                            type="button"
                            key={c.id}
                            onClick={() => selectExistingClient(c)}
                            className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-100 last:border-b-0"
                            data-testid={`client-search-result-ventes-${c.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-slate-800 truncate">{c.nom || "—"}</span>
                              {c.nombre_commandes > 0 && (
                                <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full flex-shrink-0">{c.nombre_commandes} cmd</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {c.telephone || ""} {c.telephone && (c.ville || c.adresse) && "•"} {c.ville || c.adresse || ""}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mois</Label>
                    <Input
                      type="month"
                      value={formData.mois}
                      onChange={(e) => setFormData({ ...formData, mois: e.target.value })}
                      required
                      data-testid="mois-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Quantité</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.quantite}
                      onChange={(e) => setFormData({ ...formData, quantite: e.target.value })}
                      required
                      data-testid="quantite-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Produit *</Label>
                  <Select
                    value={formData.produit_id}
                    onValueChange={(value) => setFormData({ ...formData, produit_id: value })}
                  >
                    <SelectTrigger data-testid="produit-select">
                      <SelectValue placeholder="Sélectionner un produit" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(productsByModel).map(([modele, prods]) => (
                        <div key={modele}>
                          <div className="px-2 py-1.5 text-sm font-semibold text-slate-500 bg-slate-100">
                            {modele}
                          </div>
                          {prods.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.couleur} - {p.prix_b2c_ttc}€ (Stock: {p.stock_restant})
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedProduit && (
                    <p className="text-xs text-slate-500">
                      SKU: {selectedProduit.sku} | Coût: {selectedProduit.cost_unit}€
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Canal</Label>
                    <Select
                      value={formData.canal}
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        canal: value,
                        type_prix: value === "B2C" ? "TTC" : "HT"
                      })}
                    >
                      <SelectTrigger data-testid="canal-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="B2C">B2C (Particulier)</SelectItem>
                        <SelectItem value="B2B">B2B (Professionnel)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Type prix</Label>
                    <Select
                      value={formData.type_prix}
                      onValueChange={(value) => setFormData({ ...formData, type_prix: value })}
                    >
                      <SelectTrigger data-testid="type-prix-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TTC">TTC</SelectItem>
                        <SelectItem value="HT">HT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Coût override (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder={selectedProduit ? selectedProduit.cost_unit.toString() : "Auto"}
                      value={formData.cost_override}
                      onChange={(e) => setFormData({ ...formData, cost_override: e.target.value })}
                      data-testid="cost-override-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Prix override (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder={selectedProduit ? 
                        (formData.canal === "B2C" ? selectedProduit.prix_b2c_ttc : selectedProduit.prix_b2b_ht).toString() 
                        : "Auto"}
                      value={formData.prix_override}
                      onChange={(e) => setFormData({ ...formData, prix_override: e.target.value })}
                      data-testid="prix-override-input"
                    />
                  </div>
                </div>

                {/* Mode de paiement */}
                <div className="grid grid-cols-2 gap-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="space-y-2">
                    <Label className="text-emerald-900 font-semibold">Mode de paiement</Label>
                    <Select
                      value={formData.mode_paiement}
                      onValueChange={(v) => setFormData({ ...formData, mode_paiement: v, virement_beneficiaire: v === "virement" ? formData.virement_beneficiaire : "" })}
                    >
                      <SelectTrigger data-testid="mode-paiement-select" className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="liquide">💶 Liquide (cash)</SelectItem>
                        <SelectItem value="virement">🏦 Virement bancaire</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.mode_paiement === "virement" && (
                    <div className="space-y-2">
                      <Label className="text-emerald-900 font-semibold">Bénéficiaire</Label>
                      <Select
                        value={formData.virement_beneficiaire || ""}
                        onValueChange={(v) => setFormData({ ...formData, virement_beneficiaire: v })}
                      >
                        <SelectTrigger data-testid="virement-beneficiaire-select" className="bg-white"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cocoon_elec">Cocoon-Elec</SelectItem>
                          <SelectItem value="livreur">Livreur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-vente-btn">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Enregistrer la vente
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Récap paiements */}
      {paiementsSummary && (paiementsSummary.total > 0) && (
        <Card className="bg-gradient-to-r from-emerald-50 via-indigo-50 to-violet-50 border-indigo-200" data-testid="paiements-summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-sm text-slate-700 font-semibold flex items-center gap-1">
                💰 Paiements reçus{filterMois ? ` (${filterMois})` : " (tous mois)"} :
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-emerald-200 shadow-sm">
                <span className="text-lg">💶</span>
                <div>
                  <div className="text-xs text-slate-500">Cash</div>
                  <div className="font-bold text-emerald-700 text-lg">{paiementsSummary.total_cash.toFixed(2)} €</div>
                  <div className="text-[10px] text-slate-500">{paiementsSummary.count_cash} vente(s)</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-blue-200 shadow-sm">
                <span className="text-lg">🏦</span>
                <div>
                  <div className="text-xs text-slate-500">Virement total</div>
                  <div className="font-bold text-blue-700 text-lg">{paiementsSummary.total_virement.toFixed(2)} €</div>
                  <div className="text-[10px] text-slate-500">{paiementsSummary.count_virement} vente(s)</div>
                </div>
              </div>
              {paiementsSummary.total_virement_cocoon > 0 && (
                <div className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200">
                  🏦 Cocoon-Elec : <strong>{paiementsSummary.total_virement_cocoon.toFixed(2)} €</strong>
                </div>
              )}
              {paiementsSummary.total_virement_livreur > 0 && (
                <div className="text-xs bg-violet-50 px-2 py-1 rounded border border-violet-200">
                  🏦 Livreur : <strong className="text-violet-700">{paiementsSummary.total_virement_livreur.toFixed(2)} €</strong>
                </div>
              )}
              {paiementsSummary.count_non_defini > 0 && (
                <div className="text-xs bg-amber-50 px-2 py-1 rounded border border-amber-200">
                  ⚠️ Non défini : {paiementsSummary.total_non_defini.toFixed(2)} € ({paiementsSummary.count_non_defini} vente(s))
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-slate-400" />
            <div className="flex items-center gap-2">
              <Label className="text-slate-600">Filtrer par mois:</Label>
              <Input
                type="month"
                value={filterMois}
                onChange={(e) => setFilterMois(e.target.value)}
                className="w-48"
                data-testid="filter-mois"
              />
              {filterMois && (
                <Button variant="ghost" size="sm" onClick={() => setFilterMois("")}>
                  Effacer
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-slate-600">Client:</Label>
              <Input
                type="text"
                placeholder="Rechercher un client..."
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                className="w-56"
                data-testid="filter-client"
              />
              {filterClient && (
                <Button variant="ghost" size="sm" onClick={() => setFilterClient("")}>
                  Effacer
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Quantité totale</p>
            <p className="text-2xl font-bold text-slate-800">{totals.quantite}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">CA HT</p>
            <p className="text-2xl font-bold text-indigo-600">{totals.ca_ht.toFixed(2)} €</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">CA TTC</p>
            <p className="text-2xl font-bold text-blue-600">{totals.ca_ttc.toFixed(2)} €</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Marge HT</p>
            <p className="text-2xl font-bold text-emerald-600">{totals.marge_ht.toFixed(2)} €</p>
          </CardContent>
        </Card>
        {modelStats.slice(0, 2).map((m, i) => (
          <Card key={m.modele} className={cardThemes[i % cardThemes.length].card}>
            <CardContent className="p-4">
              <p className={`text-sm ${cardThemes[i % cardThemes.length].label}`}>{m.modele} vendues</p>
              <p className={`text-2xl font-bold ${cardThemes[i % cardThemes.length].value}`} data-testid={`kpi-model-${m.modele.toLowerCase()}`}>{m.quantite || 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs for List and Stats */}
      <Tabs defaultValue="liste" className="space-y-4">
        <TabsList>
          <TabsTrigger value="liste" data-testid="tab-liste">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Liste des ventes
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">
            <Palette className="w-4 h-4 mr-2" />
            Stats par Couleur
          </TabsTrigger>
        </TabsList>

        <TabsContent value="liste">
          {/* Table — Chaises & Services en 2 tableaux séparés */}
          <Tabs defaultValue="chaises" className="space-y-4">
            <TabsList className="w-full sm:w-auto">
              <TabsTrigger value="chaises" data-testid="subtab-chaises">
                Chaises ({ventesFiltered.filter(v => v.type_item !== 'service').length})
              </TabsTrigger>
              <TabsTrigger value="services" data-testid="subtab-services">
                Services ({ventesFiltered.filter(v => v.type_item === 'service').length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="chaises">
              <Card data-testid="ventes-table-card">
                <CardHeader>
                  <CardTitle className="text-lg">Ventes de chaises ({ventesFiltered.filter(v => v.type_item !== 'service').length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : ventesFiltered.filter(v => v.type_item !== 'service').length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Aucune vente de chaise</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Mois</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Modèle</TableHead>
                        <TableHead>Couleur</TableHead>
                        <TableHead>Canal</TableHead>
                        <TableHead>Paiement</TableHead>
                        <TableHead className="text-right">Qté</TableHead>
                        <TableHead className="text-right">CA HT</TableHead>
                        <TableHead className="text-right">TVA</TableHead>
                        <TableHead className="text-right">CA TTC</TableHead>
                        <TableHead className="text-right">Marge HT</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ventesFiltered.filter(v => v.type_item !== 'service').map((vente) => (
                        <TableRow key={vente.id} data-testid={`vente-row-${vente.id}`}>
                          <TableCell className="font-medium">{vente.mois}</TableCell>
                          <TableCell data-testid={`vente-client-${vente.id}`}>
                            {vente.client_nom ? <span className="text-slate-700">{vente.client_nom}</span> : <span className="text-slate-300">—</span>}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{vente.sku}</TableCell>
                          <TableCell>{vente.modele}</TableCell>
                          <TableCell>{vente.couleur}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              vente.canal === 'B2C' 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {vente.canal}
                            </span>
                          </TableCell>
                          <TableCell>
                            <button type="button" onClick={() => openEditPaiement(vente)} className="hover:opacity-75 transition" title="Modifier le mode de paiement" data-testid={`edit-paiement-${vente.id}`}>
                              {vente.mode_paiement === "liquide" && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">💵 Cash</span>
                              )}
                              {vente.mode_paiement === "virement" && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${vente.virement_beneficiaire && vente.virement_beneficiaire.startsWith("livreur") ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-blue-700"}`} title={vente.iban_recu || ""}>
                                  🏦 Vir. {vente.virement_beneficiaire === "livreur" ? "Livreur" : vente.virement_beneficiaire === "cocoon_elec" ? "Cocoon" : vente.virement_beneficiaire && vente.virement_beneficiaire.startsWith("livreur:") ? vente.virement_beneficiaire.slice(8) : vente.virement_beneficiaire === "cocoon" ? "Cocoon" : ""}
                                </span>
                              )}
                              {!vente.mode_paiement && (
                                <span className="text-xs text-slate-400 underline decoration-dotted">— définir</span>
                              )}
                            </button>
                          </TableCell>
                          <TableCell className="text-right">{vente.quantite}</TableCell>
                          <TableCell className="text-right">{vente.ca_ht.toFixed(2)} €</TableCell>
                          <TableCell className="text-right">{vente.tva_collectee.toFixed(2)} €</TableCell>
                          <TableCell className="text-right">{vente.ca_ttc.toFixed(2)} €</TableCell>
                          <TableCell className="text-right font-medium text-emerald-600">
                            {vente.marge_ht.toFixed(2)} €
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(vente.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-vente-${vente.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
            </TabsContent>

            <TabsContent value="services">
              <Card data-testid="ventes-services-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="text-amber-500">✨</span>
                    Ventes de services ({ventesFiltered.filter(v => v.type_item === 'service').length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ventesFiltered.filter(v => v.type_item === 'service').length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <p>Aucune vente de service pour l'instant</p>
                      <p className="text-xs mt-1">Les lignes de devis reconnues comme services apparaîtront ici.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Mois</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Service</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Paiement</TableHead>
                            <TableHead className="text-right">Qté</TableHead>
                            <TableHead className="text-right">CA HT</TableHead>
                            <TableHead className="text-right">TVA</TableHead>
                            <TableHead className="text-right">CA TTC</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {ventesFiltered.filter(v => v.type_item === 'service').map((vente) => (
                            <TableRow key={vente.id} data-testid={`service-vente-${vente.id}`}>
                              <TableCell className="font-medium">{vente.mois}</TableCell>
                              <TableCell>
                                {vente.client_nom ? <span className="text-slate-700">{vente.client_nom}</span> : <span className="text-slate-300">—</span>}
                              </TableCell>
                              <TableCell>
                                <span className="font-medium text-amber-700">{vente.modele}</span>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500 max-w-xs truncate">{vente.description_libre || "—"}</TableCell>
                              <TableCell>
                                {vente.mode_paiement === "liquide" && <span className="px-2 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700">💶 Liquide</span>}
                                {vente.mode_paiement === "virement" && <span className="px-2 py-1 rounded-full text-xs bg-indigo-50 text-indigo-700">🏦 Virement</span>}
                                {!vente.mode_paiement && <span className="text-xs text-slate-400">—</span>}
                              </TableCell>
                              <TableCell className="text-right">{vente.quantite}</TableCell>
                              <TableCell className="text-right">{vente.ca_ht.toFixed(2)} €</TableCell>
                              <TableCell className="text-right">{vente.tva_collectee.toFixed(2)} €</TableCell>
                              <TableCell className="text-right font-bold text-amber-600">{vente.ca_ttc.toFixed(2)} €</TableCell>
                              <TableCell>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(vente.id)} className="text-red-500 hover:bg-red-50" data-testid={`delete-service-vente-${vente.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="stats">
          <Card data-testid="ventes-stats-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Palette className="w-5 h-5 text-indigo-600" />
                Statistiques par Modèle et Couleur
              </CardTitle>
            </CardHeader>
            <CardContent>
              {colorStats.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Palette className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune vente enregistrée</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rang</TableHead>
                        <TableHead>Modèle</TableHead>
                        <TableHead>Couleur</TableHead>
                        <TableHead className="text-right">Quantité Vendue</TableHead>
                        <TableHead className="text-right">CA HT</TableHead>
                        <TableHead className="text-right">Marge HT</TableHead>
                        <TableHead className="text-right">% du Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {colorStats.map((stat, index) => (
                        <TableRow key={index} data-testid={`stat-row-${index}`}>
                          <TableCell>
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' :
                              index === 1 ? 'bg-slate-100 text-slate-600' :
                              index === 2 ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-50 text-slate-500'
                            }`}>
                              {index + 1}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              stat.modele === 'MILAN' 
                                ? 'bg-indigo-100 text-indigo-700' 
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {stat.modele}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">{stat.couleur}</TableCell>
                          <TableCell className="text-right">
                            <span className="font-bold text-lg">{stat.quantite}</span>
                          </TableCell>
                          <TableCell className="text-right">{stat.ca_ht.toFixed(2)} €</TableCell>
                          <TableCell className="text-right text-emerald-600 font-medium">
                            {stat.marge_ht.toFixed(2)} €
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-slate-600">
                              {totals.quantite > 0 ? ((stat.quantite / totals.quantite) * 100).toFixed(1) : 0}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary by Model (dynamique) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {modelStats.length === 0 && (
              <p className="text-slate-400 text-sm">Aucune vente enregistrée.</p>
            )}
            {modelStats.map((m, i) => {
              const t = cardThemes[i % cardThemes.length];
              return (
                <Card key={m.modele} className={t.card.replace(/bg-\w+-50 /, "")} >
                  <CardHeader className={t.head}>
                    <CardTitle className={`text-lg ${t.title}`}>{m.modele} - Résumé</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Quantité vendue:</span>
                      <span className={`font-bold ${t.value}`}>{m.quantite || 0} chaises</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">CA HT:</span>
                      <span className="font-medium">{(m.ca_ht || 0).toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Marge HT:</span>
                      <span className="font-medium text-emerald-600">{(m.marge_ht || 0).toFixed(2)} €</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog modification paiement */}
      <Dialog open={editPaiementOpen} onOpenChange={setEditPaiementOpen}>
        <DialogContent className="max-w-md" data-testid="edit-paiement-dialog">
          <DialogHeader>
            <DialogTitle>Modifier le mode de paiement</DialogTitle>
          </DialogHeader>
          {editPaiementTarget && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Mode de paiement</Label>
                <Select
                  value={editPaiementTarget.mode_paiement}
                  onValueChange={(v) => setEditPaiementTarget({ ...editPaiementTarget, mode_paiement: v })}
                >
                  <SelectTrigger data-testid="edit-mode-paiement-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="liquide">💶 Liquide (cash)</SelectItem>
                    <SelectItem value="virement">🏦 Virement bancaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editPaiementTarget.mode_paiement === "virement" && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Bénéficiaire</Label>
                  <Select
                    value={editPaiementTarget.virement_beneficiaire || ""}
                    onValueChange={(v) => setEditPaiementTarget({ ...editPaiementTarget, virement_beneficiaire: v })}
                  >
                    <SelectTrigger data-testid="edit-virement-beneficiaire-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cocoon_elec">Cocoon-Elec</SelectItem>
                      <SelectItem value="livreur">Livreur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPaiementOpen(false)}>Annuler</Button>
            <Button onClick={submitEditPaiement} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-paiement-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Ventes;
